import createBlock from '../../../utils/createBlock';
import { FIRST_BLOCK_OFFSET_X, FIRST_BLOCK_OFFSET_Y } from './spawnOrigin';
import { GROUP_KEY_PREFIX } from '../constants';
import { findVariable, resolveOrCreateVariable } from './variables';
import {
    isChoiceAsk, buildChoiceAskBlock, buildAskSayBlock, buildTerminalStatementBlock, buildAnnouncementBlock, buildJumpBlock,
    buildConditionBlock, buildCategorizationBlock, buildSetVarBlock, buildApiBlock, buildScenarioBlock,
} from './blockBuilders';

export const createStepBlocks = ({ addBlock, addVariable, updateBlockData }) => {
    // Register the block before the updateBlockData calls that can throw
    const createStepBlock = async (step, position, x, y, knownVariables, stepMap) => {
        let blockDef;

        switch (step.type) {
            case 'ask':
            case 'say':
                if (isChoiceAsk(step)) {
                    blockDef = buildChoiceAskBlock(step);
                    if (step.variable) {
                        const variable = await resolveOrCreateVariable(step.variable, 'text', knownVariables, addVariable);
                        blockDef.tree.data.variable_redirect = variable;
                        blockDef.server.statement_redirect_var = variable.name;
                    }
                    break;
                }
                blockDef = step.next ? buildAskSayBlock(step) : buildTerminalStatementBlock(step);
                if (step.type === 'ask') {
                    const variable = await resolveOrCreateVariable(step.variable, 'text', knownVariables, addVariable);
                    blockDef.tree.data.variable_redirect = variable;
                    blockDef.server.statement_redirect_var = variable.name;
                }
                break;
            case 'announcement':
                blockDef = step.next ? buildAnnouncementBlock(step) : buildTerminalStatementBlock(step);
                break;
            case 'jump':
                blockDef = buildJumpBlock();
                break;
            case 'condition':
                blockDef = buildConditionBlock();
                await resolveOrCreateVariable(step.variable, 'text', knownVariables, addVariable);
                break;
            case 'categorization':
                blockDef = buildCategorizationBlock(step);
                await resolveOrCreateVariable(step.variable, 'text', knownVariables, addVariable);
                if (step.save_result_in) {
                    await resolveOrCreateVariable(step.save_result_in, 'text', knownVariables, addVariable);
                }
                break;
            case 'set_var':
                blockDef = buildSetVarBlock();
                for (const assignment of step.assignments) {
                    await resolveOrCreateVariable(assignment.variable, 'text', knownVariables, addVariable);
                }
                break;
            case 'api':
                blockDef = buildApiBlock();
                for (const mapping of (step.response_mapping || [])) {
                    await resolveOrCreateVariable(mapping.variable, 'text', knownVariables, addVariable);
                }
                break;
            default:
                return null;
        }

        const created = createBlock({ ...blockDef }, x, y, FIRST_BLOCK_OFFSET_X + position.x, FIRST_BLOCK_OFFSET_Y + position.y);

        const accepted = await addBlock(created.block, created.blockServer, created.children);
        stepMap[step.id] = { objectId: created.block.object_id, children: created.children, type: step.type };
        if (accepted === false) throw new Error('the server refused to create the block');

        if (step.type === 'condition') {
            await updateBlockData(created.block.object_id, { condition: step.condition }, { condition: step.condition });
        }
        else if (step.type === 'categorization') {
            const variable = findVariable(knownVariables, step.variable);
            if (!variable) throw new Error('variable "' + step.variable + '" was not found');
            const branches = step.branches.map((branch, i) => {
                const child = created.children.find(c => c.text === 'Branch_' + i);
                return { branch_id: child?.object_id, values: branch.values, forbidden: branch.forbidden, is_default: false };
            });
            const defaultChild = created.children.find(c => c.text === 'Default');
            branches.push({ branch_id: defaultChild?.object_id, values: [], forbidden: [], is_default: true });

            const patchData = { variable, branches };
            const patchServer = { keyword_var: variable.name, branches };
            if (step.save_result_in) {
                const redirectVariable = findVariable(knownVariables, step.save_result_in);
                if (!redirectVariable) throw new Error('variable "' + step.save_result_in + '" was not found');
                patchData.variable_redirect = redirectVariable;
                patchServer.keyword_redirect_var = redirectVariable.name;
            }
            await updateBlockData(created.block.object_id, patchData, patchServer);
        }
        else if (step.type === 'set_var') {
            const rows = step.assignments.map((assignment, i) => ({
                value: assignment.value,
                is_operation: assignment.is_operation,
                index: String(i),
                variable: findVariable(knownVariables, assignment.variable),
            }));
            const rowsServer = step.assignments.map((assignment, i) => ({
                variable_value: assignment.value,
                is_operation: assignment.is_operation,
                index: String(i),
                variable_name: assignment.variable,
            }));
            await updateBlockData(created.block.object_id, { variables: rows }, { variables: rowsServer });
        }
        else if (step.type === 'api') {
            const bodyType = step.body_type;
            const body = bodyType === 'raw' ? { raw: step.body } : step.body;
            const responses = (step.response_mapping || []).map(m => ({ key: m.path, value: m.variable.toUpperCase() }));
            const patchData = {
                url: step.url,
                api_method_type: { name: step.method },
                api_body_type: { name: bodyType },
                headers: step.headers,
                body,
                responses,
            };
            const patchServer = {
                api_url: step.url,
                api_method_type: step.method,
                api_body_type: bodyType,
                api_headers: step.headers,
                api_body: body,
                api_responses: responses,
            };
            await updateBlockData(created.block.object_id, patchData, patchServer);
        }

        return created;
    };

    const createGroupHeader = async (group, position, x, y, stepMap) => {
        const created = createBlock({ ...buildScenarioBlock(group) }, x, y, FIRST_BLOCK_OFFSET_X + position.x, FIRST_BLOCK_OFFSET_Y + position.y);

        const accepted = await addBlock(created.block, created.blockServer, created.children);
        stepMap[GROUP_KEY_PREFIX + group.id] = { objectId: created.block.object_id, children: created.children, type: 'scenario' };
        if (accepted === false) throw new Error('the server refused to create the block');
    };

    return { createStepBlock, createGroupHeader };
};
